import { config } from '../config'
import type { AnalysisResult, InsightsData, InsightReport, StratumHierarchy } from './types'

// API Response types matching server DTOs
interface ServerResponse {
    success: boolean
    sceneJson?: string
    semanticTopology?: string
    semanticTopologyLabel?: string
    nodeCount?: number
    edgeCount?: number
    entityCount?: number
    actionCount?: number
    locusCount?: number
    error?: string
    insights?: InsightsData
    insightReport?: InsightReport
    stratumHierarchy?: StratumHierarchy
    theme?: { roleAction: string; roleSource: string; roleResult: string; roleIntermediate: string; roleHub: string; roleLocus: string; accent: string; accentSubtle: string }
    statements?: import('./types').ParsedStatement[]
}

// Convert server response to local AnalysisResult format
function convertServerResponse(response: ServerResponse): AnalysisResult {
    if (!response.success) {
        throw new Error(response.error || 'Server returned unsuccessful response')
    }

    return {
        sceneJson: response.sceneJson ?? undefined,
        semanticTopology: response.semanticTopology ?? undefined,
        semanticTopologyLabel: response.semanticTopologyLabel ?? undefined,
        nodeCount: response.nodeCount || 0,
        edgeCount: response.edgeCount || 0,
        entityCount: response.entityCount || 0,
        actionCount: response.actionCount || 0,
        locusCount: response.locusCount || 0,
        insights: response.insights ? {
            topology: response.insights.topology,
            topologyExact: response.insights.topologyExact,
            topologyEvidence: response.insights.topologyEvidence,
            fiedlerValue: response.insights.fiedlerValue,
        } : undefined,
        insightReport: response.insightReport,
        stratumHierarchy: response.stratumHierarchy,
        theme: response.theme,
        statements: response.statements,
    }
}

// Production mode: Call server API
async function analyzeWithServer(source: string, isDark: boolean = true, view?: string, seedColor?: string, themeName?: string, direction?: string): Promise<AnalysisResult> {
    const response = await fetch(`${config.apiUrl}/playground/analyze`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            source,
            sessionId: config.sessionId,
            isDark,
            ...(view && { view }),
            ...(seedColor && { seedColor }),
            ...(themeName && { theme: themeName }),
            ...(direction && { direction }),
        })
    })

    // Read body for all responses — server returns domain errors (Lexer, Parser, Type)
    // as HTTP 400 with a JSON body containing the actual error message.
    let data: ServerResponse
    try {
        data = await response.json()
    } catch {
        // Body wasn't valid JSON — true HTTP-level failure (502, 503, etc.)
        throw new Error(`Server error: ${response.status} ${response.statusText}`)
    }

    if (!data.success) {
        throw new Error(data.error || 'Analysis failed')
    }

    return convertServerResponse(data)
}

// Render a clean SVG via core/render (the single source of truth for rendering).
// Dev mode uses the KMP JS bundle directly (renderSvgLocal); prod mode goes through
// the MCP /render tool. The picker's full state flows through:
//   - isDark (light/dark mode toggle)
//   - seedColor (Material 3 with custom seed hex)
//   - themeName (curated preset like "Blueprint", "Swiss")
//   - structureLayers (bitmask: subsystems=1, pipelines=2, cycles=4, forks=8,
//     joins=16, hubs=32, deadlock=64, overflow=128; default 255 = all on, 0 = hide all)
//
// Renderer theme priority: themeName > seedColor > isDark fallback (themeSet default).
//
// For MCP (prod path) all four map to tool args: `theme` carries the preset name
// or "dark"/"light", `color` carries the seed hex, `structure_layers` carries the
// bitmask as a stringified int. McpToolRoutes.kt resolves them correctly.
export async function renderSvg(source: string, isDark: boolean = true, view?: string, seedColor?: string, themeName?: string, structureLayers: number = 255, direction?: string): Promise<string | null> {
    if (import.meta.env.VITE_API_MODE === 'prod') {
        const args: Record<string, string> = { source, quality: 'max', type: 'svg' }
        args.isDark = String(isDark)
        if (themeName) args.theme = themeName
        else args.theme = isDark ? 'dark' : 'light'
        if (seedColor) args.color = seedColor
        if (view) args.view = view
        args.structure_layers = String(structureLayers)
        if (direction) args.direction = direction
        const response = await fetch(`${config.apiUrl}/mcp`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                jsonrpc: '2.0', id: Date.now(),
                method: 'tools/call',
                params: { name: 'render', arguments: args },
            }),
        })
        const body = await response.json()
        const svg = body?.result?.content?.[1]?.text
        return svg?.startsWith('<svg') ? svg : null
    }
    const { renderSvgLocal } = await import('./localParser')
    return await renderSvgLocal(source, isDark, view, structureLayers, seedColor, themeName, direction)
}

/**
 * Render a PNG diagram via the server-side Batik rasterizer.
 * Only works in prod mode (requires the Ktor HTTP server). In dev mode, returns
 * null — callers should fall back to the browser canvas rasterization path.
 *
 * The server returns the PNG as a base64-encoded image content block with
 * audience:user. We decode it to a Blob for direct download.
 */
export async function renderPng(
    source: string,
    isDark: boolean = true,
    view?: string,
    seedColor?: string,
    themeName?: string,
    structureLayers: number = 255,
): Promise<Blob | null> {
    if (import.meta.env.VITE_API_MODE !== 'prod') return null
    const args: Record<string, string> = { source, quality: 'max', type: 'png' }
    args.isDark = String(isDark)
    if (themeName) args.theme = themeName
    else args.theme = isDark ? 'dark' : 'light'
    if (seedColor) args.color = seedColor
    if (view) args.view = view
    args.structure_layers = String(structureLayers)
    const response = await fetch(`${config.apiUrl}/mcp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            jsonrpc: '2.0', id: Date.now(),
            method: 'tools/call',
            params: { name: 'render', arguments: args },
        }),
    })
    const body = await response.json()
    const imageContent = body?.result?.content?.[1]
    if (imageContent?.type === 'image' && imageContent.data) {
        const binaryString = atob(imageContent.data)
        const bytes = new Uint8Array(binaryString.length)
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i)
        }
        return new Blob([bytes], { type: 'image/png' })
    }
    return null
}

// Main analyze function - switches based on mode.
// In prod mode, the local parser is never imported — no KMP JS library
// is shipped, keeping the pipeline internals closed-source on the server.
export async function analyze(source: string, isDark: boolean = true, view?: string, seedColor?: string, themeName?: string, direction?: string): Promise<AnalysisResult> {
    if (import.meta.env.VITE_API_MODE === 'prod') {
        return analyzeWithServer(source, isDark, view, seedColor, themeName, direction)
    }
    const { parseENLocal } = await import('./localParser')
    return await parseENLocal(source, isDark, view, seedColor, themeName, direction)
}

export type { AnalysisResult } from './types'

// ── Refactoring API ──

export async function refactorRename(source: string, oldName: string, newName: string): Promise<{ success: boolean; source?: string; occurrences?: number; error?: string }> {
    const res = await fetch(`${config.apiUrl}/playground/refactor/rename`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source, oldName, newName }),
    })
    return res.json()
}

export async function refactorFind(source: string, name: string): Promise<{ occurrences: { line: number; column: number; text: string }[] }> {
    const res = await fetch(`${config.apiUrl}/playground/refactor/find`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source, name }),
    })
    return res.json()
}
